import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { ChoiceGroup } from '../ChoiceGroup';
import { useProductChoiceGroup } from './useProductChoiceGroup';
import type { ProductChoiceRevertRow } from './ProductChoiceGroup.types';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useProductToUIMapping } from '@apps/card/components/ProductUIConfig/useProductToUIMapping';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { Product } from '@apps/card/components/ProductUIConfig/product.types';
import { useTranslation } from '@shared/core';
import { useCustomizationContext } from '../../useCardCustomization';
import { getDimensions } from '@apps/card/utils';
import { formatProductSizeTitle, useCardSizeLabels } from '@apps/card/utils/cardSizeName';

const isProduct = (input: unknown): input is StationeryProduct & Product => !!input;

const ActionItem = ({ selected, title, price }: { selected: boolean; title: string; price?: string }) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      flex={1}
      paddingX={3}
    >
      <TextV2
        typographyVariant="body1"
        color="mono12"
        textTransform="capitalize"
        fontWeight={selected ? 600 : 400}
        whiteSpace="nowrap"
      >
        {title}
      </TextV2>
      {price && (
        <TextV2
          typographyVariant="body1"
          color="mono12"
          fontWeight={selected ? 600 : 400}
          whiteSpace="nowrap"
        >
          {price}
        </TextV2>
      )}
    </Flex>
  );
};

type ProductChoiceGroupProps = Readonly<{
  draftId: string;
  revertRow?: ProductChoiceRevertRow;
  /** Dims the group while the caller has work of its own in flight, as a switch does. */
  busy?: boolean;
}>;

type ProductChoiceGroupContentProps = Readonly<{
  isSwitching: boolean;
  selectProduct: (productId: string) => void;
  choiceProducts: StationeryProduct[];
  revertRow?: ProductChoiceRevertRow;
}>;

const ProductChoiceGroupContent = ({ isSwitching, selectProduct, choiceProducts, revertRow }: ProductChoiceGroupContentProps) => {
  const { activeProduct, priceCalculator } = useStationeryProductPricingContext();
  const { getProductWithUIMapping } = useProductToUIMapping();
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const { tq } = useTranslation('stationery');
  // Every switch target lives on the draft's own template, so the row titles
  // take the draft's category and size: the same "Postcard 4x6" / "Magnet 4x6"
  // / "Classic Paper 5x7" the PDP picker shows, rather than the bare product
  // name (which would call a postcard "Classic Paper").
  const [templateCategory] = useCustomizationContext(d => d.stationeryTemplateCategory, true);
  const [card] = useCustomizationContext(d => d.config.card, true);
  const { width: widthInches, height: heightInches } = getDimensions(card);
  const sizeLabels = useCardSizeLabels();

  const products = choiceProducts.map(p => getProductWithUIMapping(p)).filter(isProduct);

  const revertProductId = revertRow ? products.find(p => p.productType === revertRow.productType)?.id : undefined;
  const isRevertRow = (productId: string) => !!revertProductId && productId === revertProductId;

  const handleSelect = (productId: string) => {
    // Return early rather than falling through: `selectProduct` records a product switch, and an
    // undo counted as one would show up in the switcher's from/to reporting as a real conversion.
    if (isRevertRow(productId)) {
      if (!revertRow?.isDisabled) revertRow?.onSelect();
      return;
    }
    selectProduct(productId);
  };

  return (
    <Box
      opacity={isSwitching ? 0.6 : 1}
      pointerEvents={isSwitching ? 'none' : 'auto'}
      transition="opacity 0.15s ease"
    >
      <ChoiceGroup
        title={tq`Product`}
        getName={() => (products.length === 1 ? activeProduct.translations.formatName : '')}
        value={activeProduct.id}
        minHeight={55}
        choices={products.map(p => p.id)}
        setValue={handleSelect}
        // `isSwitching` dims the group and blocks the pointer, but a button stays in the tab order
        // and Enter still fires it -- and a keyboard select landing mid-undo would start a second
        // product mutation racing the one in flight, since the two paths hold separate locks.
        isChoiceDisabled={productId => isSwitching || (isRevertRow(productId) && !!revertRow?.isDisabled)}
        getChoiceDescribedBy={productId => (isRevertRow(productId) ? revertRow?.describedById : undefined)}
        getOpacity={productId => (isRevertRow(productId) && revertRow?.isDisabled ? 0.5 : 1)}
        columns={1}
      >
        {products.map(product => {
          const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, undefined, true);
          const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
          const priceDisplay = priceData.unitPriceInMinorUnits === 0 ? unitPriceString : tq`From ${unitPriceString}`;

          return (
            <ActionItem
              key={product.id}
              selected={product.id === activeProduct.id}
              title={formatProductSizeTitle(templateCategory, widthInches, heightInches, sizeLabels, product)}
              price={priceDisplay}
            />
          );
        })}
      </ChoiceGroup>
    </Box>
  );
};
// The pdp screen choicegroup is not used here because it involves a lot of complicated issues, such as cross app imports and exports,
//contexts and other dependencies and also the callback functions are not the same.
//It was simply not worth the effort to re-use the pdp screen choicegroup and not in the scope of this PR.
export const ProductChoiceGroup = ({ draftId, revertRow, busy }: ProductChoiceGroupProps) => {
  const { selectProduct, isSwitching, canSelectProduct, choiceProducts } = useProductChoiceGroup({ draftId, alsoListProductType: revertRow?.productType });
  if (!canSelectProduct) return null;
  return (
    <ProductChoiceGroupContent
      isSwitching={isSwitching || !!busy}
      selectProduct={selectProduct}
      choiceProducts={choiceProducts}
      revertRow={revertRow}
    />
  );
};
